import { useState, type ReactNode } from 'react';
import { Flex } from '@clariness/flex';
import { Icon, Text } from '@clariness/clarikit-react';
import { Link } from '@clariness/link';
import { useIntl } from 'react-intl';
import { LabeledContent } from 'components/content';
import { QuestionDto, ReferralDto, ReScreenerQuestionDto } from 'common/api';
import * as _ from 'lodash-es';
import { sortBy } from 'lodash-es';
import { Tooltip } from '@clariness/tooltip';
import {
  getOfflineScreenerAnswer,
  getRelevantOfflineScreener,
  hasBsaScoreQuestions,
  mapBSAOnlineQuestionToScore,
  mapBSAOfflineQuestionToScore,
} from 'common/question/question';
import { getAnswerValue } from 'common/answer/answer-value';
import { calculateBMIScore } from 'common/answer/calculate-bmi';
import { Image } from '@clariness/image';
import { Box } from '@clariness/box';
import { Services } from 'services';
import { BoxLoader } from 'components/loaders';
import { InfoIcon } from '@clariness/clarikit-icons';

type PatientDetailsScreenerProps = {
  referralId?: string;
};

function mapOfflineQuestionToAnswer(
  question: QuestionDto | ReScreenerQuestionDto,
  referral?: ReferralDto
) {
  if (undefined === referral) {
    return {};
  }
  const relevantOfflineScreener = getRelevantOfflineScreener(referral);
  const answers = getOfflineScreenerAnswer(relevantOfflineScreener, question);

  return {
    id: question.id,
    question: question.question,
    questionType: question.type,
    visibleToInvestigators: question.visibleToInvestigators,
    answers: answers?.values
      ? _.reject(
          answers?.values.map((answer) => getAnswerValue(question, answer)),
          _.isNil
        )
      : [],
    answerValues: answers,
  };
}

function mapOnlineQuestionToAnswer(
  question: QuestionDto,
  referral?: ReferralDto
) {
  if (undefined === referral) {
    return {};
  }
  const answers = referral.patientScreener.answers?.find(
    (answer) => answer.questionId === question.id
  );

  return {
    id: question.id,
    question: question.question,
    questionType: question.type,
    visibleToInvestigators: question.visibleToInvestigators,
    answers: answers?.values
      ? _.reject(
          answers?.values.map((answer) => {
            const questionAnswerChoice = question.choices?.find(
              (choice) => choice.id === answer.questionChoiceId
            );
            return {
              value: getAnswerValue(question, answer),
              imagePublicUrl: questionAnswerChoice?.mediaAsset?.publicUrl,
              imageAltText: questionAnswerChoice?.altText,
            };
          }),
          _.isNil
        )
      : [],
    answerValues: answers,
    mediaAsset: {
      publicUrl: question?.mediaAsset?.publicUrl,
      altText: question?.mediaAsset?.altText,
    },
  };
}

function HeaderAction({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <Link
      sx={{
        color: 'color-green-primary',
        fontWeight: 'bold',
        textDecoration: 'none',
      }}
      onClick={onClick}
    >
      {label}
    </Link>
  );
}

function createLabeledContent(
  screenerQuestionsAndAnswer: Array<any>,
  bsaScoreTotal: Array<any>,
  intl: any,
  preScreenerAnswersTranslationId: string,
  preScreenerAnswersTranslationDefault: string,
  action?: ReactNode
) {
  if (screenerQuestionsAndAnswer.length === 0 && !action) {
    return <div />;
  }

  return (
    <LabeledContent
      label={intl.formatMessage({
        id: preScreenerAnswersTranslationId,
        defaultMessage: preScreenerAnswersTranslationDefault,
      })}
      action={action}
    >
      <Flex
        sx={{
          flexDirection: 'column',
          gap: '12px',
        }}
      >
        <Flex
          sx={{
            flexDirection: 'column',
            gap: '20px',
            pb: '20px',
          }}
        >
          {screenerQuestionsAndAnswer
            .filter(
              (screenerQuestion) => screenerQuestion.visibleToInvestigators
            )
            .map((screenerQuestion, index) => (
              <Flex
                key={screenerQuestion.id}
                sx={{
                  flexDirection: 'column',
                  gap: '5px',
                  mb: '16px',
                }}
              >
                <Flex gap={2}>
                  <Text as="p" fontWeight="bold">{`${index + 1}. `}</Text>
                  <Box
                    as="p"
                    sx={{
                      '& > p, ul': {
                        m: 0,
                      },
                      fontWeight: '400',
                    }}
                    dangerouslySetInnerHTML={{
                      __html: screenerQuestion.question,
                    }}
                  ></Box>
                </Flex>
                {screenerQuestion?.mediaAsset?.publicUrl && (
                  <Image
                    src={screenerQuestion.mediaAsset.publicUrl}
                    alt={screenerQuestion.mediaAsset.altText}
                    sx={{
                      maxHeight: '300px',
                      maxWidth: '300px',
                      borderRadius: '5px',
                    }}
                  />
                )}
                {screenerQuestion.answers.map((answer: any) => {
                  return (
                    <>
                      <Box
                        sx={{
                          fontWeight: '400',
                          fontSize: '0.875rem',
                        }}
                        dangerouslySetInnerHTML={{
                          __html: answer?.value ? answer.value : answer,
                        }}
                      />
                      {answer?.imagePublicUrl && (
                        <Image
                          src={answer.imagePublicUrl}
                          alt={answer.imageAltText}
                          sx={{
                            maxHeight: '160px',
                            maxWidth: '160px',
                            borderRadius: '5px',
                            mb: '5px',
                          }}
                        />
                      )}
                    </>
                  );
                })}
                {screenerQuestion.questionType === QuestionDto.type.BMI && (
                  <Text as="p" fontWeight="bold">
                    BMI : {calculateBMIScore(screenerQuestion.answerValues)}
                  </Text>
                )}
              </Flex>
            ))}

          {bsaScoreTotal.map((bst) => (
            <Flex
              key={bst.label}
              sx={{
                flexDirection: 'column',
                gap: '5px',
              }}
            >
              <Tooltip
                position="top right"
                trigger={
                  <Flex
                    sx={{
                      alignItems: 'center',
                      gap: '5px',
                    }}
                  >
                    <Text as="p" variant="bodySm">
                      {bst.label}
                    </Text>
                    <Box>
                      <Icon source={InfoIcon} size="small" />
                    </Box>
                  </Flex>
                }
              >
                <Text as="p" variant="bodySm">
                  This is the total amount of BSA values entered and equates to
                  the % of the BSA across the patient’s body.
                </Text>
              </Tooltip>
              <Text as="p">{bst.score}</Text>
            </Flex>
          ))}
        </Flex>
      </Flex>
    </LabeledContent>
  );
}
function getOfflineOrReScreenerQuestionsSorted(
  referral: ReferralDto | undefined
) {
  if (referral?.reScreener) {
    return sortBy(referral.reScreener.reScreenerQuestions, ['order']);
  }
  return sortBy(referral?.screener.questions, ['order']).filter((question) => {
    return question.isOffline;
  });
}

export const PatientDetailsScreener = ({
  referralId,
}: PatientDetailsScreenerProps) => {
  const intl = useIntl();
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);
  const [visibleScreener, setVisibleScreener] = useState<'online' | 'offline'>(
    'offline'
  );
  const { screener, isLoading } = Services.Referrals.useGetScreener(referralId);
  const { patientSummary } =
    Services.Referrals.useGetPatientSummary(referralId);
  const hasPatientSummary = Boolean(patientSummary?.body?.trim());

  const onlineScreenerQuestions = sortBy(screener?.screener.questions, [
    'order',
  ]).filter((question) => {
    return !question.isOffline;
  });
  const offlineScreenerQuestions =
    getOfflineOrReScreenerQuestionsSorted(screener);

  const screenerOnlineQuestions =
    onlineScreenerQuestions.map((question) =>
      mapOnlineQuestionToAnswer(question, screener)
    ) || [];
  const screenerOfflineQuestions =
    offlineScreenerQuestions.map((question) =>
      mapOfflineQuestionToAnswer(question, screener)
    ) || [];

  const totalBsaScoreLabel = 'Total BSA Score';
  const onlineBsaTotalScore = [];
  if (hasBsaScoreQuestions(onlineScreenerQuestions)) {
    const totalOnlineBSAScore = onlineScreenerQuestions
      .map((question) => mapBSAOnlineQuestionToScore(question, screener))
      .reduce((partialSum, a) => partialSum + a, 0);

    onlineBsaTotalScore.push({
      label: totalBsaScoreLabel,
      score: [totalOnlineBSAScore.toString()],
    });
  }

  const offlineBsaTotalScore = [];
  if (hasBsaScoreQuestions(offlineScreenerQuestions)) {
    const totalOfflineBSAScore = offlineScreenerQuestions
      .map((question) => mapBSAOfflineQuestionToScore(question, screener))
      .reduce((partialSum, a) => partialSum + a, 0);

    offlineBsaTotalScore.push({
      label: totalBsaScoreLabel,
      score: [totalOfflineBSAScore.toString()],
    });
  }

  const onlineScreeningLabel = intl.formatMessage({
    id: 'is.patient_details_modal.online_screening_title',
    defaultMessage: 'Online Screening',
  });
  const offlineScreeningLabel = intl.formatMessage({
    id: 'prescreen_offline_answers_content',
    defaultMessage: 'Offline Screening',
  });
  const patientSummaryLabel = intl.formatMessage({
    id: 'is.patient_details_modal.patient_summary',
    defaultMessage: 'Patient Summary',
  });
  const closeLabel = intl.formatMessage({
    id: 'is.patient_details_modal.patient_summary_close',
    defaultMessage: 'Close',
  });
  const showOnlineColumn = !isSummaryOpen || visibleScreener === 'online';
  const showOfflineColumn = !isSummaryOpen || visibleScreener === 'offline';
  const summaryLink =
    hasPatientSummary && !isSummaryOpen ? (
      <HeaderAction
        label={patientSummaryLabel}
        onClick={() => {
          setVisibleScreener('offline');
          setIsSummaryOpen(true);
        }}
      />
    ) : undefined;
  const switchScreenerLink = isSummaryOpen ? (
    <HeaderAction
      label={
        visibleScreener === 'offline'
          ? onlineScreeningLabel
          : offlineScreeningLabel
      }
      onClick={() =>
        setVisibleScreener(visibleScreener === 'offline' ? 'online' : 'offline')
      }
    />
  ) : undefined;

  return isLoading ? (
    <BoxLoader />
  ) : (
    <Flex sx={{ flexDirection: 'row', gap: '48px' }}>
      {showOnlineColumn && (
        <Flex
          sx={{
            width: '50%',
          }}
        >
          {createLabeledContent(
            screenerOnlineQuestions,
            onlineBsaTotalScore,
            intl,
            'is.patient_details_modal.online_screening_title',
            'Online Screening',
            isSummaryOpen ? switchScreenerLink : undefined
          )}
        </Flex>
      )}
      {showOfflineColumn && (
        <Flex
          sx={{
            width: '50%',
          }}
        >
          {createLabeledContent(
            screenerOfflineQuestions,
            offlineBsaTotalScore,
            intl,
            'prescreen_offline_answers_content',
            'Offline Screening',
            isSummaryOpen ? switchScreenerLink : summaryLink
          )}
        </Flex>
      )}
      {isSummaryOpen && hasPatientSummary && (
        <Flex
          sx={{
            width: '50%',
          }}
        >
          <LabeledContent
            label={patientSummaryLabel}
            action={
              <HeaderAction
                label={closeLabel}
                onClick={() => setIsSummaryOpen(false)}
              />
            }
          >
            <Box
              sx={{
                fontWeight: '400',
                '& > p:first-of-type': { marginTop: 0 },
                '& p:last-child': { marginBottom: 0 },
                '& ul, & ol': { paddingLeft: '20px' },
                '& a': { color: 'color-green-primary' },
              }}
              dangerouslySetInnerHTML={{ __html: patientSummary?.body || '' }}
            />
          </LabeledContent>
        </Flex>
      )}
    </Flex>
  );
};
